import { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  ArrowRight, Check, AlertTriangle, FileSpreadsheet, 
  Building2, Plus, ChevronDown, ChevronUp, X
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';

interface ColumnMapping {
  sourceColumn: string;
  targetField: string;
  autoMapped: boolean;
}

interface VendorMatch {
  name: string;
  exists: boolean;
  count: number;
}

interface VendorAction {
  name: string;
  action: 'create' | 'ignore';
}

interface ColumnMappingModalProps {
  open: boolean;
  onClose: () => void;
  onProceed: (mappings: Record<string, string>, vendorActions: Record<string, 'create' | 'ignore'>) => void;
  sourceColumns: string[];
  existingVendors: { id: string; name: string }[];
  detectedVendors: string[];
  isLoading?: boolean;
}

// Target fields that can be mapped to
const TARGET_FIELDS = [
  { value: 'account_number', label: 'Account Number', required: true },
  { value: 'property_name', label: 'Property Name', required: true },
  { value: 'property_code', label: 'Property Code', required: false },
  { value: 'unit_number', label: 'Unit Number', required: true },
  { value: 'commodity_type', label: 'Commodity Type', required: true },
  { value: 'vendor_name', label: 'Vendor Name', required: true },
  { value: 'meter_number', label: 'Meter Number', required: false },
  { value: 'gl_code', label: 'GL Code', required: false },
  { value: 'service_address', label: 'Service Address', required: false },
  { value: 'billing_address', label: 'Billing Address', required: false },
  { value: 'notes', label: 'Notes', required: false },
];

// Auto-mapping rules
const AUTO_MAP_RULES: Record<string, string[]> = {
  'account_number': ['account #', 'account number', 'account_number', 'acct #', 'acct'],
  'property_name': ['property name', 'property', 'building', 'location'],
  'property_code': ['property code', 'building code', 'prop code'],
  'unit_number': ['unit #', 'unit number', 'unit', 'apt', 'apartment'],
  'commodity_type': ['commodity', 'utility type', 'service type', 'utility'],
  'vendor_name': ['vendor', 'vendor name', 'supplier', 'supplier name', 'provider'],
  'meter_number': ['meter #', 'meter number', 'meter'],
  'gl_code': ['gl code', 'gl account', 'account code', 'gl'],
  'service_address': ['service address', 'address', 'service location'],
  'billing_address': ['billing address', 'bill to', 'billing location'],
  'notes': ['notes', 'comments', 'remarks'],
};

export const ColumnMappingModal = ({
  open,
  onClose,
  onProceed,
  sourceColumns,
  existingVendors,
  detectedVendors,
  isLoading = false,
}: ColumnMappingModalProps) => {
  const [mappings, setMappings] = useState<ColumnMapping[]>([]);
  const [vendorActions, setVendorActions] = useState<Record<string, 'create' | 'ignore'>>({});
  const [showVendorDetails, setShowVendorDetails] = useState(true);

  // Auto-map columns on load
  useEffect(() => {
    if (sourceColumns.length > 0) {
      const autoMapped: ColumnMapping[] = sourceColumns.map(col => {
        const normalizedCol = col.toLowerCase().replace(/[*#]/g, '').trim();
        
        // Find matching target field
        let targetField = '';
        for (const [field, patterns] of Object.entries(AUTO_MAP_RULES)) {
          if (patterns.some(p => normalizedCol.includes(p) || p.includes(normalizedCol))) {
            targetField = field;
            break;
          }
        }
        
        return {
          sourceColumn: col,
          targetField,
          autoMapped: !!targetField,
        };
      });
      
      setMappings(autoMapped);
    }
  }, [sourceColumns]);

  // Analyze vendor matches
  const vendorAnalysis = useMemo(() => {
    const existingNames = new Set(existingVendors.map(v => v.name.toLowerCase().trim()));
    const vendorCounts: Record<string, number> = {};
    
    detectedVendors.forEach(v => {
      const key = v.toLowerCase().trim();
      vendorCounts[key] = (vendorCounts[key] || 0) + 1;
    });
    
    const uniqueVendors = [...new Set(detectedVendors.map(v => v.trim()))];
    
    const matches: VendorMatch[] = uniqueVendors.map(name => ({
      name,
      exists: existingNames.has(name.toLowerCase().trim()),
      count: vendorCounts[name.toLowerCase().trim()] || 1,
    }));
    
    return {
      total: uniqueVendors.length,
      matched: matches.filter(m => m.exists).length,
      unmatched: matches.filter(m => !m.exists).length,
      list: matches,
    };
  }, [existingVendors, detectedVendors]);

  // Initialize vendor actions for unmatched vendors (default to 'create')
  useEffect(() => {
    if (vendorAnalysis.unmatched > 0) {
      const unmatchedVendors = vendorAnalysis.list.filter(v => !v.exists);
      const newActions: Record<string, 'create' | 'ignore'> = {};
      unmatchedVendors.forEach(v => {
        if (!vendorActions[v.name]) {
          newActions[v.name] = 'create'; // Default to create
        } else {
          newActions[v.name] = vendorActions[v.name];
        }
      });
      if (Object.keys(newActions).length > 0) {
        setVendorActions(prev => ({ ...prev, ...newActions }));
      }
    }
  }, [vendorAnalysis]);

  // Update mapping
  const updateMapping = (sourceColumn: string, targetField: string) => {
    setMappings(prev => prev.map(m => 
      m.sourceColumn === sourceColumn 
        ? { ...m, targetField, autoMapped: false }
        : m
    ));
  };

  // Check if all required fields are mapped
  const requiredFieldsMapped = useMemo(() => {
    const mappedFields = new Set(mappings.map(m => m.targetField));
    const requiredFields = TARGET_FIELDS.filter(f => f.required).map(f => f.value);
    return requiredFields.every(f => mappedFields.has(f));
  }, [mappings]);

  // Get available target fields (not already mapped)
  const getAvailableFields = (currentTarget: string) => {
    const usedFields = new Set(mappings.map(m => m.targetField).filter(t => t && t !== currentTarget));
    return TARGET_FIELDS.filter(f => !usedFields.has(f.value) || f.value === currentTarget);
  };

  // Handle proceed
  const handleProceed = () => {
    const mappingObj: Record<string, string> = {};
    mappings.forEach(m => {
      // Only include mappings where targetField is explicitly set and not 'unmapped'
      // Empty strings and 'unmapped' should be excluded
      if (m.targetField && m.targetField !== 'unmapped' && m.targetField.trim() !== '') {
        mappingObj[m.sourceColumn] = m.targetField;
      }
    });
    console.log('📋 Column Mapping - Final mapping object:', mappingObj);
    console.log('📋 Column Mapping - Excluded columns:', 
      mappings.filter(m => !m.targetField || m.targetField === 'unmapped' || m.targetField.trim() === '')
        .map(m => m.sourceColumn)
    );
    onProceed(mappingObj, vendorActions);
  };

  // Update vendor action
  const updateVendorAction = (vendorName: string, action: 'create' | 'ignore') => {
    setVendorActions(prev => ({
      ...prev,
      [vendorName]: action,
    }));
  };

  // Get required fields list for display
  const requiredFields = TARGET_FIELDS.filter(f => f.required);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5" />
            Column Mapping
          </DialogTitle>
          <DialogDescription>
            Map your file columns to system fields. Required fields are marked with *.
          </DialogDescription>
        </DialogHeader>

        {/* Required Fields Indicator */}
        <div className="flex-shrink-0 mb-2 p-2 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-md">
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle className="h-4 w-4 text-blue-600 dark:text-blue-400 shrink-0" />
            <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
              Required Fields:
            </span>
          </div>
          <div className="flex flex-wrap gap-2 mt-1">
            {requiredFields.map(field => (
              <Badge 
                key={field.value} 
                variant="destructive" 
                className="text-xs"
              >
                {field.label} *
              </Badge>
            ))}
          </div>
        </div>

        <ScrollArea className="flex-1 min-h-0 pr-4">
          <div className="space-y-4 pb-4">
            {/* Mapping Table */}
            <div className="border rounded-md overflow-hidden">
              <div className="grid grid-cols-[1fr,auto,1fr] gap-2 p-2 bg-muted/50 text-xs font-medium sticky top-0 z-10">
                <span>Source Column</span>
                <span></span>
                <span>Target Field</span>
              </div>
              <div className="divide-y max-h-[400px] overflow-y-auto">
                {mappings.map((mapping, index) => {
                  const selectedField = TARGET_FIELDS.find(f => f.value === mapping.targetField);
                  return (
                    <div key={mapping.sourceColumn} className="grid grid-cols-[1fr,auto,1fr] gap-2 p-2 items-center hover:bg-muted/30">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-sm truncate">{mapping.sourceColumn}</span>
                        {mapping.autoMapped && (
                          <Badge variant="secondary" className="text-[10px] px-1 py-0 shrink-0">Auto</Badge>
                        )}
                      </div>
                      <ArrowRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      <Select
                        value={mapping.targetField || 'unmapped'}
                        onValueChange={(v) => updateMapping(mapping.sourceColumn, v === 'unmapped' ? '' : v)}
                      >
                        <SelectTrigger className="h-8 text-sm min-w-[180px]">
                          <SelectValue placeholder="Select field...">
                            {selectedField ? (
                              <span className="flex items-center gap-1">
                                {selectedField.label}
                                {selectedField.required && (
                                  <span className="text-destructive font-bold">*</span>
                                )}
                              </span>
                            ) : (
                              'Select field...'
                            )}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent className="max-h-[300px]">
                          <SelectItem value="unmapped" className="text-muted-foreground">
                            — Don't import —
                          </SelectItem>
                          {getAvailableFields(mapping.targetField).map(field => (
                            <SelectItem key={field.value} value={field.value}>
                              <div className="flex items-center gap-2">
                                <span>{field.label}</span>
                                {field.required && (
                                  <Badge variant="destructive" className="text-[10px] px-1 py-0 h-4">
                                    Required
                                  </Badge>
                                )}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Required Fields Warning */}
            {!requiredFieldsMapped && (
              <div className="flex items-start gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-md text-sm">
                <AlertTriangle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
                <div className="flex-1">
                  <span className="text-destructive font-medium block mb-1">
                    Missing Required Fields:
                  </span>
                  <div className="flex flex-wrap gap-2">
                    {requiredFields
                      .filter(f => !mappings.some(m => m.targetField === f.value))
                      .map(field => (
                        <Badge key={field.value} variant="destructive" className="text-xs">
                          {field.label}
                        </Badge>
                      ))}
                  </div>
                </div>
              </div>
            )}

            {/* Vendor Analysis Section */}
            {detectedVendors.length > 0 && (
              <Collapsible open={showVendorDetails} onOpenChange={setShowVendorDetails}>
                <div className="border rounded-md">
                  <CollapsibleTrigger className="w-full p-2 flex items-center justify-between hover:bg-muted/50">
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4" />
                      <span className="text-sm font-medium">Vendor Analysis</span>
                      <Badge variant="outline" className="text-[10px]">
                        {vendorAnalysis.total} vendors found
                      </Badge>
                    </div>
                    {showVendorDetails ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="p-2 pt-0 space-y-2">
                      {/* Stats */}
                      <div className="grid grid-cols-2 gap-2">
                        <div className="flex items-center gap-2 p-2 bg-green-500/10 rounded text-xs">
                          <Check className="h-3.5 w-3.5 text-green-600" />
                          <span className="text-green-700">{vendorAnalysis.matched} matched vendors</span>
                        </div>
                        <div className="flex items-center gap-2 p-2 bg-amber-500/10 rounded text-xs">
                          <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />
                          <span className="text-amber-700">{vendorAnalysis.unmatched} new vendors</span>
                        </div>
                      </div>

                      {/* Vendor List with Per-Vendor Actions */}
                      {vendorAnalysis.unmatched > 0 && (
                        <div className="space-y-2">
                          <p className="text-xs font-medium text-muted-foreground">
                            Unmatched vendors - Choose action for each:
                          </p>
                          <div className="space-y-2 max-h-48 overflow-y-auto border rounded-md p-2">
                            {vendorAnalysis.list.filter(v => !v.exists).map(v => (
                              <div 
                                key={v.name} 
                                className="flex items-center justify-between p-2 bg-muted/30 rounded border"
                              >
                                <div className="flex items-center gap-2 flex-1 min-w-0">
                                  <Plus className="h-3.5 w-3.5 text-amber-600 shrink-0" />
                                  <span className="text-sm font-medium truncate">{v.name}</span>
                                  <Badge variant="outline" className="text-[10px] shrink-0">
                                    {v.count} row{v.count > 1 ? 's' : ''}
                                  </Badge>
                                </div>
                                <div className="flex items-center gap-2 ml-2 shrink-0">
                                  <Button
                                    type="button"
                                    variant={vendorActions[v.name] === 'create' ? 'default' : 'outline'}
                                    size="sm"
                                    className="h-7 text-xs px-2"
                                    onClick={() => updateVendorAction(v.name, 'create')}
                                  >
                                    <Plus className="h-3 w-3 mr-1" />
                                    Create
                                  </Button>
                                  <Button
                                    type="button"
                                    variant={vendorActions[v.name] === 'ignore' ? 'default' : 'outline'}
                                    size="sm"
                                    className="h-7 text-xs px-2"
                                    onClick={() => updateVendorAction(v.name, 'ignore')}
                                  >
                                    <X className="h-3 w-3 mr-1" />
                                    Ignore
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                          <p className="text-xs text-muted-foreground">
                            <strong>Create:</strong> New vendor will be added to Vendor Management<br />
                            <strong>Ignore:</strong> Row will be imported without creating vendor record
                          </p>
                        </div>
                      )}
                    </div>
                  </CollapsibleContent>
                </div>
              </Collapsible>
            )}
          </div>
        </ScrollArea>

        <DialogFooter className="flex-shrink-0 mt-4 border-t pt-4">
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button 
            onClick={handleProceed} 
            disabled={isLoading || !requiredFieldsMapped}
          >
            {isLoading ? 'Processing...' : 'Proceed with Import'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
